import { useEffect, useState } from 'react'

import i18n from 'i18n-literally'

import { styled } from '@mui/material/styles'
import { guessCountryByPartialPhoneNumber } from 'react-international-phone'

import CodeInput from '~/src/UI/Shared/Form/CodeInput'
import T from '~/src/UI/Shared/Typography'

const RESEND_SECONDS = 60

/** Hides every digit but the last four: "+52 *** *** 3265". */
export const maskPhoneNumber = (phoneNumber: string): string => {
  const digits = phoneNumber.replace(/\D/g, '')
  if (digits.length <= 4) return phoneNumber

  const { country } = guessCountryByPartialPhoneNumber({ phone: phoneNumber })
  const dialCode = country?.dialCode ?? ''
  const national = digits.slice(dialCode.length)
  const hidden = national.slice(0, -4).replace(/\d/g, '*')
  const groups = hidden.match(/.{1,3}/g) ?? []

  return [dialCode ? `+${dialCode}` : '', ...groups, national.slice(-4)].filter(Boolean).join(' ')
}

export const formatCountdown = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, Math.trunc(totalSeconds))
  return `${Math.floor(safeSeconds / 60)}:${String(safeSeconds % 60).padStart(2, '0')}`
}

const Content = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '1ch',
  // The form's fields sit in 80px-tall controls over a 56px input, so the rest
  // of the dialog is spaced by the 24px that leaves over.
  marginTop: theme.spacing(3),
  textAlign: 'center',
}))

const Counter = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'center',
}))

const InlineButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  color: theme.palette.primary.main,
  cursor: 'pointer',
  font: 'inherit',
  padding: 0,
}))

export interface PhoneVerificationProps {
  readonly error?: string
  readonly phoneNumber: string
  readonly onEdit: () => void
  readonly onComplete?: (code: string) => void
  readonly onResend?: () => void
}

const PhoneVerification = ({ error, phoneNumber, onEdit, onComplete, onResend }: PhoneVerificationProps) => {
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS)

  useEffect(() => {
    if (secondsLeft <= 0) return undefined
    const timer = setTimeout(() => setSecondsLeft(current => current - 1), 1000)
    return () => clearTimeout(timer)
  }, [secondsLeft])

  return (
    <Content data-testid="phone-verification">
      <T>
        {i18n`We've sent a 6-digit verification code to the number:`} {phoneNumber}{' '}
      </T>

      <CodeInput error={Boolean(error)} onComplete={onComplete} />

      {error ? (
        <T.Tiny data-testid="code-error" sx={{ color: 'error.main' }}>
          {error}
        </T.Tiny>
      ) : null}

      {/* The counter stays put under the error, otherwise a wrong code hides
          Resend Code for good and leaves no way to ask for another one. */}
      <Counter>
        <T.Tiny>{i18n`Didn't get the code?`}</T.Tiny>
        {secondsLeft > 0 ? (
          <T.Tiny data-testid="resend-countdown">
            {i18n`Resend Code in`} {formatCountdown(secondsLeft)}
          </T.Tiny>
        ) : (
          <InlineButton
            type="button"
            data-testid="resend-code-button"
            onClick={() => {
              setSecondsLeft(RESEND_SECONDS)
              onResend?.()
            }}
          >
            {i18n`Resend Code`}
          </InlineButton>
        )}
      </Counter>
    </Content>
  )
}

export default PhoneVerification
